import { Controller } from 'react-hook-form';

import { Alert, Box, MenuItem, Select, Skeleton, useTheme } from '@mui/material';
import {
  ContactTemplateSelectProps,
  ReferenceInputAndLabelSkeletonProps,
  ReferenceSelectAndLabelProps,
} from 'features/Connect/features/ConnectATSIntegration/types/referenceTypes';
import { ErrorText } from '../../../Scorecard/Create/components/ScorecardQuestionAndAnswerSection';
import { Label } from 'sharedComponents/Label';

const ReferenceInputAndLabelSkeleton: React.FC<ReferenceInputAndLabelSkeletonProps> = ({
  dataTestId,
  hasLabel,
}) => {
  const theme = useTheme();

  return (
    <Box>
      {hasLabel && (
        <Skeleton
          data-testid={`${dataTestId}-loading-skeleton`}
          sx={{
            fontSize: theme.typography.body2.fontSize,
            lineHeight: theme.spacing(2.5),
            marginBottom: theme.spacing(0.625),
          }}
        />
      )}
      <Skeleton variant="rectangular" height={theme.spacing(6.625)} width="100%" />
    </Box>
  );
};

export const ReferenceSelectAndLabel: React.FC<ReferenceSelectAndLabelProps> = ({
  dataTestId,
  defaultValue,
  hasError,
  isLoading,
  items,
  labelText,
  onChange,
  placeholderText,
  shouldBeDisabled,
  value,
}) => {
  const theme = useTheme();

  if (isLoading) {
    return <ReferenceInputAndLabelSkeleton hasLabel={false} dataTestId={dataTestId} />;
  }

  const calculatedShouldBeDisabled = shouldBeDisabled && !defaultValue && !value;

  return (
    <>
      {labelText && (
        <Label
          sx={{ marginBottom: theme.spacing(1) }}
          dataTestId={`${dataTestId}-label`}
          labelText={labelText}
        />
      )}
      <Select
        data-testid={dataTestId}
        disabled={calculatedShouldBeDisabled}
        displayEmpty
        error={hasError}
        fullWidth
        onChange={onChange}
        value={value || defaultValue || ''}
        sx={{
          height: theme.spacing(5.25),
          '& .MuiSelect-select': {
            color: theme.palette.text.tertiary,
          },
        }}
      >
        <MenuItem value="">{placeholderText}</MenuItem>
        {items?.map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {item.name || item.title}
          </MenuItem>
        ))}
      </Select>
      {hasError && <ErrorText dataTestId={`${dataTestId}-required-error`} />}
    </>
  );
};

export const ContactTemplateSelect: React.FC<ContactTemplateSelectProps> = ({
  dataTestId,
  defaultValue = '',
  formId,
  handleChange,
  isGetError,
  isLoading,
  items,
  labelText,
  placeholderText,
}) => {
  if (isLoading) {
    return <ReferenceInputAndLabelSkeleton hasLabel={true} dataTestId={dataTestId} />;
  }

  if (isGetError) {
    return (
      <Alert data-testid={`${dataTestId}-error-message`} severity="error">
        Something went wrong
      </Alert>
    );
  }

  return (
    <Box>
      <Controller
        name={formId}
        defaultValue={defaultValue}
        rules={{
          required: true,
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <ReferenceSelectAndLabel
              dataTestId={dataTestId}
              defaultValue={String(defaultValue) || ''}
              hasError={Boolean(error)}
              isLoading={isLoading}
              items={items}
              labelText={labelText}
              onChange={(event) => handleChange(event, onChange)}
              placeholderText={placeholderText}
              value={typeof value === 'object' ? value?.id : value || ''}
            />
          );
        }}
      />
    </Box>
  );
};
